import dayjs from 'dayjs';
import { GetArticleBySlug_getArticleBySlug } from 'api/articles/types/GetArticleBySlug';
import { MentionLink } from 'helpers/linkFirstMentions';
import { getEnvLink } from 'helpers/routes';
import {
  cleanArticleContent,
  getArticleKeywords,
} from 'helpers/structured-data';

// Matches DESC_LIMIT in helpers/athleteProfileSeo.ts.
const DESC_LIMIT = 170;

type ArticleSeoInput =
  | {
      title?: string | null;
      content?: string | null;
      metaDescription?: string | null;
      store?: { storeDetails?: { storeName?: string | null } | null } | null;
    }
  | null
  | undefined;

/**
 * Cut at the last whole word that fits. Falls back to a hard cut when the last
 * space sits so early that trimming to it would throw away most of the budget.
 */
const truncateOnWord = (value: string, limit: number): string => {
  if (value.length <= limit) return value;

  const slice = value.slice(0, limit - 1);
  const lastSpace = slice.lastIndexOf(' ');
  const cut = lastSpace > limit * 0.6 ? slice.slice(0, lastSpace) : slice;

  return `${cut.replace(/[\s,.;:—-]+$/, '')}…`;
};

/**
 * Uses the article's written meta description, otherwise its opening lines,
 * otherwise a title + publisher sentence. Never returns a constant string —
 * a shared description is worse than no description.
 */
export const buildArticleMetaDescription = (
  article: ArticleSeoInput
): string => {
  const written = article?.metaDescription?.trim();

  if (written) {
    return truncateOnWord(written, DESC_LIMIT);
  }

  const storeName = article?.store?.storeDetails?.storeName || 'MILLIONS';
  const body = cleanArticleContent(article?.content);

  if (body) {
    return truncateOnWord(body, DESC_LIMIT);
  }

  const title = (article?.title || '').trim();

  if (title) {
    return truncateOnWord(
      `${title} — read the full article from ${storeName} on MILLIONS.`,
      DESC_LIMIT
    );
  }

  return truncateOnWord(
    `Read the latest article from ${storeName} on MILLIONS.`,
    DESC_LIMIT
  );
};

type GeneratedArticleSeoInput =
  | Pick<
      GetArticleBySlug_getArticleBySlug,
      | '__typename'
      | 'createdAt'
      | 'updatedAt'
      | 'imageLabel'
      | 'imageAlt'
      | 'focusKeyword'
      | 'hashtags'
      | 'sourceInterviewee'
      | 'sourceInterviewer'
    >
  | null
  | undefined;

export type ArticleOpenGraph = {
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  tags: string[];
};

const isGeneratedArticle = (article: GeneratedArticleSeoInput): boolean =>
  article?.__typename === 'GeneratedArticle';

const toIsoDate = (value: string | null | undefined): string | undefined =>
  value && dayjs(value).isValid() ? dayjs(value).toISOString() : undefined;

/** Generated articles carry a written alt; the caption is the fallback. */
export const getArticleImageAlt = (
  article: GeneratedArticleSeoInput
): string | undefined =>
  (isGeneratedArticle(article) &&
    (article?.imageAlt?.trim() || article?.focusKeyword?.trim())) ||
  article?.imageLabel ||
  undefined;

/** Profile links for the interviewee and host of a generated article. */
export const getArticleMentionLinks = (
  article: GeneratedArticleSeoInput
): MentionLink[] => {
  if (!isGeneratedArticle(article)) return [];

  return [article?.sourceInterviewee, article?.sourceInterviewer]
    .filter((person) => !!person?.slug)
    .map((person) => ({
      name:
        person?.storeDetails?.storeName ||
        `${person?.firstName || ''} ${person?.lastName || ''}`.trim(),
      href: `/${person?.slug}`,
    }));
};

export const buildArticleOpenGraph = (
  article: GeneratedArticleSeoInput,
  storeSlug: string | null | undefined
): ArticleOpenGraph | undefined => {
  if (!isGeneratedArticle(article)) return undefined;

  const publishedTime = toIsoDate(article?.createdAt);

  return {
    publishedTime,
    modifiedTime: toIsoDate(article?.updatedAt) || publishedTime,
    author: storeSlug ? `${getEnvLink()}/${storeSlug}` : undefined,
    tags: getArticleKeywords((article?.hashtags || []).map(({ name }) => name)),
  };
};
